import { QueryResult } from "@/api/utils/types"
import { SoDataTableHeader } from "@/types/components/SoDataTable"
import { SoExportCsvButtonData } from "@/components/dataTable/SoExportCsvButton.vue"
import dayjs from "dayjs"
import { InstallCodeExtraQueryResult } from "@/services/operations/InstallCodesService"
import { IInstallCodeExtra } from "./installCode.types"

interface IAsnCSVExporter {
  getConfig: () => {
    data: SoExportCsvButtonData
    headers: SoDataTableHeader[]
    fileName: () => string
  }
}

export const headers: SoDataTableHeader[] = [
  {
    title: "SourceID",
    key: "sourceId",
  },
  {
    title: "SourceCounter",
    key: "sourceCounter",
  },
  {
    title: "Device ID",
    key: "deviceId",
  },
  {
    title: "MAP ID",
    key: "mapId",
  },
  {
    title: "Supplier ID",
    key: "supplierId",
  },
  {
    title: "Meter Serial Number",
    key: "msn",
  },
  {
    title: "Device Manufacturer",
    key: "manufacturer",
  },
  {
    title: "Device Model",
    key: "deviceModel",
  },
  {
    title: "Device Type",
    key: "deviceType",
  },
  {
    title: "Manufacturer Year",
    key: "manufacturerYear",
  },
  {
    title: "Manufacturer Month",
    key: "manufacturerMonth",
  },
  {
    title: "Manufacturer Batch Number",
    key: "manufacturerBatchNumber",
  },
  {
    title: "Batch Size",
    key: "batchSize",
  },
  {
    title: "GBCS Version",
    key: "gbcsVersion",
  },
  {
    title: "SMETS CHTS Version",
    key: "smetsVersion",
  },
  {
    title: "Device Firmware Version",
    key: "firmwareVersion",
  },
  {
    title: "Firmware Version - Application",
    key: "firmwareVersionApplication",
  },
  {
    title: "Firmware Version - HAN",
    key: "firmwareVersionHan",
  },
  {
    title: "Firmware Version - Metrology",
    key: "firmwareVersionMetrology",
  },
  {
    title: "Hardware Version",
    key: "hardwareVersion",
  },
  {
    title: "ESME Variant",
    key: "esmeVariant",
  },
  {
    title: "Device Configuration ID",
    key: "configurationId",
  },
  {
    title: "Reconditioned Status",
    key: "reconditionedStatus",
  },
  {
    title: "Engineering Menu Password",
    key: "engineeringMenuPassword",
  },
  {
    title: "Zigbee MAC Address",
    key: "zigbeeMacAddress",
  },
  {
    title: "Zigbee Key",
    key: "zigbeeKey",
  },
  {
    title: "DLMS Key",
    key: "dlmsKey",
  },
  {
    title: "Organisational Certificates",
    key: "organisationalCertificates",
  },
  {
    title: "Network Operator Certificates",
    key: "networkOperatorCertificates",
  },
  {
    title: "Install Code",
    key: "installCode",
  },
  {
    title: "Scheduled Delivery Date and Time",
    key: "scheduledDelivery",
  },
  {
    title: "Delivery Location",
    key: "deliveryLocation",
  },
  {
    title: "Delivery Reference",
    key: "deliveryReference",
  },
  {
    title: "Order Reference",
    key: "orderReference",
  },
  {
    title: "Purchase Order Number",
    key: "purchaseOrderNumber",
  },
  {
    title: "Pallet Identifier",
    key: "palletIdentifier",
  },
  {
    title: "Quantity of Cartons on the Pallet",
    key: "quantityOnPallet",
  },
  {
    title: "Carton Identifier",
    key: "cartonIdentifier",
  },
  {
    title: "Quantity of Devices in Carton",
    key: "quantityInCarton",
  },
  {
    title: "Quantity of Pallets in Consignment",
    key: "quantityInConsignment",
  },
]

export class AsnCSVExporter implements IAsnCSVExporter {
  private exportQuery: QueryResult<InstallCodeExtraQueryResult> | null

  constructor(exportQuery: QueryResult<InstallCodeExtraQueryResult> | null) {
    this.exportQuery = exportQuery
  }

  public getConfig() {
    return {
      data: this.parseDataForCSV(),
      headers: headers,
      fileName: this.getFilename,
    }
  }

  private parseDataForCSV(): SoExportCsvButtonData {
    if (!this.exportQuery) return { isLoading: false, items: [] }

    const modelToCSVContent = (asnExportItem: IInstallCodeExtra) =>
      headers.reduce((acc, header) => {
        acc[header.key] = this.handleObjectMapping(asnExportItem, header)
        return acc
      }, {})

    return {
      isLoading: this.exportQuery?.isFetching,
      items: this.exportQuery?.data?.records.map(modelToCSVContent) ?? [],
    }
  }

  private handleObjectMapping(
    asnExportItem: IInstallCodeExtra,
    header: SoDataTableHeader,
  ) {
    const value = asnExportItem[header.key]
    if (typeof value === "object" && "text" in value) {
      return value.text
    }

    return value
  }

  private readonly getFilename = (): string => {
    return `asn-request-results-${dayjs().toISOString()}.csv`
  }
}
